import * as XLSX from 'xlsx'

export type SheetRow = Record<string, string | number | Date | null>

export interface SmpUploadData {
  date: string
  value: number
  maxSmp: number
  minSmp: number
  weightedAver: number
}

const getCellValue = (row: SheetRow, keys: string[]) => {
  for (const key of keys) {
    const value = row[key]
    if (value !== undefined && value !== null && value !== '') return value
  }

  return undefined
}

const toNumber = (value: unknown, fieldName: string, rowIndex: number) => {
  const normalized =
    typeof value === 'string' ? value.replace(/,/g, '').trim() : value
  const numberValue = Number(normalized)

  if (!Number.isFinite(numberValue)) {
    throw new Error(`${rowIndex}행의 ${fieldName} 값이 올바르지 않습니다.`)
  }

  return numberValue
}

const formatDateValue = (value: unknown) => {
  if (value instanceof Date) {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const day = String(value.getDate()).padStart(2, '0')
    const hour = String(value.getHours()).padStart(2, '0')
    return `${year}-${month}-${day} ${hour}:00`
  }

  return String(value ?? '').trim()
}

export const parseSheetFile = async (file: File): Promise<SheetRow[]> => {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, {
    type: 'array',
    cellDates: true,
  })
  const firstSheetName = workbook.SheetNames[0]

  if (!firstSheetName) {
    throw new Error('파일에 시트가 없습니다.')
  }

  const worksheet = workbook.Sheets[firstSheetName]
  const rows = XLSX.utils.sheet_to_json<SheetRow>(worksheet, {
    defval: null,
    raw: false,
  })

  if (rows.length === 0) {
    throw new Error('등록할 데이터가 없습니다.')
  }

  return rows
}

export const parseSmpRows = (rows: SheetRow[]): SmpUploadData[] =>
  rows.map((row, index) => {
    const rowIndex = index + 2
    const date = getCellValue(row, ['date', '날짜'])
    const value = getCellValue(row, ['value', 'smp', 'SMP'])
    const maxSmp = getCellValue(row, ['maxSmp', '일 최대'])
    const minSmp = getCellValue(row, ['minSmp', '일 최소'])
    const weightedAver = getCellValue(row, ['weightedAver', '일 가중평균'])

    if (!date) {
      throw new Error(`${rowIndex}행의 날짜 값이 없습니다.`)
    }

    return {
      date: formatDateValue(date),
      value: toNumber(value, 'smp', rowIndex),
      maxSmp: toNumber(maxSmp, '일 최대', rowIndex),
      minSmp: toNumber(minSmp, '일 최소', rowIndex),
      weightedAver: toNumber(weightedAver, '일 가중평균', rowIndex),
    }
  })

export const parseSmpFile = async (file: File): Promise<SmpUploadData[]> => {
  const rows = await parseSheetFile(file)
  return parseSmpRows(rows)
}

export const downloadSheet = (
  rows: SheetRow[],
  sheetName: string,
  fileName: string
) => {
  const worksheet = XLSX.utils.json_to_sheet(rows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
  XLSX.writeFile(workbook, fileName)
}
